import { Graphics } from 'pixi.js';
import { drawIsoBox } from '../art/IsoShapes';
import type { ColorRamp } from '../art/colors';
import type { CharacterAppearance } from '../../core/types';

/**
 * Draws a character on the isometric grid using code-drawn art.
 * Characters are ~16px wide, ~24px tall — small enough to sit at desks.
 *
 * Parts (bottom to top):
 * 1. Shadow (flat ellipse on ground)
 * 2. Legs (tiny dark box)
 * 3. Body/torso (colored box — outfit color)
 * 4. Head (skin-colored cylinder/sphere)
 * 5. Hair (colored top on head)
 */

// ─── Color Pools ─────────────────────────────────────────────

const SKIN_TONES: ColorRamp[] = [
  { highlight: 0xffe0bd, base: 0xf5c6a0, shadow: 0xd4a373, dark: 0xb8845a },
  { highlight: 0xf5d0b0, base: 0xe0b890, shadow: 0xc09870, dark: 0xa08060 },
  { highlight: 0xebc8a0, base: 0xd4a878, shadow: 0xb88858, dark: 0x986840 },
  { highlight: 0xd4a878, base: 0xb88858, shadow: 0x986840, dark: 0x785030 },
  { highlight: 0xc09060, base: 0xa07848, shadow: 0x806038, dark: 0x604828 },
  { highlight: 0x9a7050, base: 0x805838, shadow: 0x604028, dark: 0x483018 },
  { highlight: 0x7a5838, base: 0x604028, shadow: 0x483018, dark: 0x302010 },
  { highlight: 0x5a3820, base: 0x402810, shadow: 0x301808, dark: 0x200c04 },
];

const OUTFIT_COLORS: ColorRamp[] = [
  { highlight: 0x5080c0, base: 0x3868a8, shadow: 0x284888, dark: 0x183060 }, // blue
  { highlight: 0xc05050, base: 0xa03838, shadow: 0x802828, dark: 0x601818 }, // red
  { highlight: 0x50a050, base: 0x388838, shadow: 0x286828, dark: 0x184818 }, // green
  { highlight: 0xc0a050, base: 0xa88838, shadow: 0x886828, dark: 0x684818 }, // gold
  { highlight: 0x8050c0, base: 0x6838a8, shadow: 0x482888, dark: 0x301860 }, // purple
  { highlight: 0xc07050, base: 0xa85838, shadow: 0x884028, dark: 0x682818 }, // orange
  { highlight: 0x50c0c0, base: 0x38a8a8, shadow: 0x288888, dark: 0x186060 }, // teal
  { highlight: 0xc050a0, base: 0xa83888, shadow: 0x882868, dark: 0x601848 }, // pink
  { highlight: 0x606070, base: 0x484858, shadow: 0x383848, dark: 0x282838 }, // dark grey
  { highlight: 0xd0d0d8, base: 0xb0b0b8, shadow: 0x909098, dark: 0x707078 }, // light grey
  { highlight: 0xe8e0d0, base: 0xd0c8b8, shadow: 0xb0a898, dark: 0x908878 }, // cream
  { highlight: 0x404048, base: 0x303038, shadow: 0x202028, dark: 0x101018 }, // black
];

const HAIR_COLORS: ColorRamp[] = [
  { highlight: 0x403020, base: 0x302010, shadow: 0x201008, dark: 0x100804 }, // black
  { highlight: 0x6a4a30, base: 0x503820, shadow: 0x382810, dark: 0x281808 }, // dark brown
  { highlight: 0x907050, base: 0x785838, shadow: 0x584028, dark: 0x403018 }, // brown
  { highlight: 0xc0a060, base: 0xa88848, shadow: 0x887038, dark: 0x685828 }, // light brown
  { highlight: 0xe0c878, base: 0xc8b060, shadow: 0xa89848, dark: 0x888038 }, // blonde
  { highlight: 0xd0d0d0, base: 0xb0b0b0, shadow: 0x909090, dark: 0x707070 }, // grey
  { highlight: 0xf0e8e0, base: 0xe0d8d0, shadow: 0xc0b8b0, dark: 0xa09890 }, // white
  { highlight: 0xc84040, base: 0xa82828, shadow: 0x881818, dark: 0x680808 }, // red
  { highlight: 0x4488cc, base: 0x2870b0, shadow: 0x185890, dark: 0x084070 }, // blue
  { highlight: 0xcc55cc, base: 0xaa33aa, shadow: 0x882288, dark: 0x661166 }, // pink
];

function getSkinTone(index: number): ColorRamp {
  return SKIN_TONES[index % SKIN_TONES.length]!;
}

function getOutfitColor(index: number): ColorRamp {
  return OUTFIT_COLORS[index % OUTFIT_COLORS.length]!;
}

function getHairColor(index: number): ColorRamp {
  return HAIR_COLORS[index % HAIR_COLORS.length]!;
}

/**
 * Draw a character at the given screen position.
 * The character stands on the tile at (cx, cy).
 */
export function drawCharacter(
  g: Graphics,
  cx: number,
  cy: number,
  appearance: CharacterAppearance,
  state: 'idle' | 'working' | 'walking' = 'idle',
): void {
  const skin = getSkinTone(appearance.skinTone);
  const outfit = getOutfitColor(appearance.outfitColor);
  const hair = getHairColor(appearance.hairColor);

  // Shadow on ground
  g.ellipse(cx, cy, 6, 3);
  g.fill({ color: 0x000000, alpha: 0.2 });

  // Working characters lean forward slightly
  const lean = state === 'working' ? 2 : 0;

  // Shoes / feet (tiny dark box)
  drawIsoBox(g, cx, cy + lean, 0.1, 2, 0, {
    highlight: 0x404040, base: 0x303030, shadow: 0x202020, dark: 0x101010,
  });

  // Legs (slightly lighter)
  drawIsoBox(g, cx, cy + lean, 0.08, 4, 2, {
    highlight: 0x505060, base: 0x404050, shadow: 0x303040, dark: 0x202030,
  });

  // Body / torso (outfit color)
  drawIsoBox(g, cx, cy + lean, 0.14, 7, 6, outfit);

  // Arms
  if (state === 'working') {
    // Arms forward (reaching toward desk)
    drawIsoBox(g, cx - 4, cy + lean + 2, 0.04, 4, 6, outfit);
    drawIsoBox(g, cx + 4, cy + lean + 2, 0.04, 4, 6, outfit);
    // Hands (skin colored)
    drawIsoBox(g, cx - 4, cy + lean + 2, 0.03, 1, 6, skin);
    drawIsoBox(g, cx + 4, cy + lean + 2, 0.03, 1, 6, skin);
  } else if (state === 'walking') {
    // Arms swinging (offset by walk cycle)
    drawIsoBox(g, cx - 5, cy, 0.04, 5, 6, outfit);
    drawIsoBox(g, cx + 5, cy, 0.04, 5, 8, outfit);
  } else {
    // Arms at sides
    drawIsoBox(g, cx - 5, cy, 0.04, 5, 7, outfit);
    drawIsoBox(g, cx + 5, cy, 0.04, 5, 7, outfit);
  }

  // Head (skin-colored box, slightly wider than body)
  drawIsoBox(g, cx, cy + lean, 0.12, 5, 13, skin);

  // Hair (on top of head)
  const hairStyle = appearance.hairStyle % 4;
  if (hairStyle === 0) {
    // Short hair — thin cap
    drawIsoBox(g, cx, cy + lean, 0.13, 2, 18, hair);
  } else if (hairStyle === 1) {
    // Medium hair — taller
    drawIsoBox(g, cx, cy + lean, 0.14, 3, 17, hair);
  } else if (hairStyle === 2) {
    // Long hair — extends down sides
    drawIsoBox(g, cx, cy + lean, 0.15, 4, 16, hair);
    // Side hair
    drawIsoBox(g, cx - 4, cy + lean, 0.03, 3, 14, hair);
    drawIsoBox(g, cx + 4, cy + lean, 0.03, 3, 14, hair);
  } else {
    // Bald / very short — just a tiny highlight
    drawIsoBox(g, cx, cy + lean, 0.12, 1, 18, skin);
  }

  // Accessory
  if (appearance.accessory > 0 && appearance.accessory <= 3) {
    // Glasses — small highlight on head
    g.ellipse(cx - 2, cy - 15 + lean, 2, 1);
    g.stroke({ color: 0x404060, width: 0.5 });
    g.ellipse(cx + 2, cy - 15 + lean, 2, 1);
    g.stroke({ color: 0x404060, width: 0.5 });
  }
}

/**
 * Draw a character portrait (front-facing, for UI panels).
 * Larger than the iso sprite — ~32x32 area.
 */
export function drawCharacterPortrait(
  g: Graphics,
  cx: number,
  cy: number,
  appearance: CharacterAppearance,
  size: number = 32,
): void {
  const skin = getSkinTone(appearance.skinTone);
  const outfit = getOutfitColor(appearance.outfitColor);
  const hair = getHairColor(appearance.hairColor);
  const s = size / 32; // scale factor

  // Body (rounded rect)
  g.roundRect(cx - 10 * s, cy - 2 * s, 20 * s, 14 * s, 3 * s);
  g.fill({ color: outfit.base });

  // Neck
  g.rect(cx - 3 * s, cy - 6 * s, 6 * s, 6 * s);
  g.fill({ color: skin.base });

  // Head (circle)
  g.circle(cx, cy - 12 * s, 9 * s);
  g.fill({ color: skin.base });

  // Face highlight
  g.circle(cx - 2 * s, cy - 13 * s, 6 * s);
  g.fill({ color: skin.highlight, alpha: 0.3 });

  // Eyes
  g.circle(cx - 3 * s, cy - 13 * s, 1.2 * s);
  g.fill({ color: 0x202030 });
  g.circle(cx + 3 * s, cy - 13 * s, 1.2 * s);
  g.fill({ color: 0x202030 });

  // Mouth
  g.ellipse(cx, cy - 9 * s, 2 * s, 0.8 * s);
  g.fill({ color: skin.shadow });

  // Hair
  const hairStyle = appearance.hairStyle % 4;
  if (hairStyle === 0) {
    // Short
    g.arc(cx, cy - 12 * s, 9.5 * s, Math.PI, 0);
    g.fill({ color: hair.base });
  } else if (hairStyle === 1) {
    // Medium
    g.arc(cx, cy - 12 * s, 10 * s, Math.PI * 1.1, -0.1 * Math.PI);
    g.fill({ color: hair.base });
  } else if (hairStyle === 2) {
    // Long
    g.arc(cx, cy - 12 * s, 10 * s, Math.PI * 1.2, -0.2 * Math.PI);
    g.fill({ color: hair.base });
    // Side hair
    g.rect(cx - 10 * s, cy - 14 * s, 4 * s, 12 * s);
    g.fill({ color: hair.base });
    g.rect(cx + 6 * s, cy - 14 * s, 4 * s, 12 * s);
    g.fill({ color: hair.base });
  }
  // hairStyle 3 = bald, no hair drawn

  // Glasses accessory
  if (appearance.accessory > 0 && appearance.accessory <= 3) {
    g.circle(cx - 3.5 * s, cy - 13 * s, 2.5 * s);
    g.stroke({ color: 0x404060, width: 1 });
    g.circle(cx + 3.5 * s, cy - 13 * s, 2.5 * s);
    g.stroke({ color: 0x404060, width: 1 });
    g.moveTo(cx - 1 * s, cy - 13 * s);
    g.lineTo(cx + 1 * s, cy - 13 * s);
    g.stroke({ color: 0x404060, width: 1 });
  }
}
